// MYTHIVA — India-first formatting helpers.
// Locale: en-IN, Currency: INR, Numbering: lakhs & crores.

export const IN_LOCALE = "en-IN";
export const IN_CURRENCY = "INR";
export const IN_TZ = "Asia/Kolkata";

/**
 * Format a number using the Indian numbering system (1,00,000).
 * Returns "—" for null/undefined/NaN so cards never render "NaN".
 */
export function formatIndianNumber(value, options = {}) {
    const { maximumFractionDigits = 2, minimumFractionDigits = 0 } = options;
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
    return new Intl.NumberFormat(IN_LOCALE, {
        maximumFractionDigits,
        minimumFractionDigits,
    }).format(Number(value));
}

/**
 * ₹1,25,000 · full INR with rupee symbol, Indian grouping.
 */
export function formatINR(value, options = {}) {
    const { fractionDigits = 0 } = options;
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
    return new Intl.NumberFormat(IN_LOCALE, {
        style: "currency",
        currency: IN_CURRENCY,
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
    }).format(Number(value));
}

/**
 * Abbreviated INR using Indian terminology: ₹1.25 L, ₹12.5 L, ₹1.25 Cr.
 * Falls back to a plain ₹ figure for small amounts.
 */
export function formatINRCompact(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
    const n = Number(value);
    const abs = Math.abs(n);
    if (abs >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)} Cr`;
    if (abs >= 1_00_000) return `₹${(n / 1_00_000).toFixed(2)} L`;
    if (abs >= 1_000) return `₹${(n / 1_000).toFixed(1)}k`;
    return `₹${new Intl.NumberFormat(IN_LOCALE, { maximumFractionDigits: 0 }).format(n)}`;
}

/**
 * DD/MM/YYYY for date-only.
 */
export function formatIndianDate(value) {
    if (!value) return "—";
    try {
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return "—";
        return new Intl.DateTimeFormat(IN_LOCALE, {
            day: "2-digit", month: "2-digit", year: "numeric",
            timeZone: IN_TZ,
        }).format(d);
    } catch {
        return "—";
    }
}

/**
 * DD/MM/YYYY, HH:mm (24h) in Asia/Kolkata.
 */
export function formatIndianDateTime(value) {
    if (!value) return "—";
    try {
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return "—";
        return new Intl.DateTimeFormat(IN_LOCALE, {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: false,
            timeZone: IN_TZ,
        }).format(d);
    } catch {
        return "—";
    }
}
